import { useEffect } from "react";

import { Coding, ValueSetExpansionContains } from "@medplum/fhirtypes";

import { Checkbox, Drawer, Group, Stack, Textarea } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";
import isEqual from "lodash/isEqual";

import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import ValueSetSearchInput from "@/components/ValueSetSearchInput";
import {
  EncounterAnswersFormType,
  EncounterDiagnosesFormType,
} from "@/encounters/zod-schemas/encounter-answers";
import { ICD10ValueSets } from "@/fhir/enums/icd";
import { showSystemErrorNotification } from "@/utils/notifications";

interface DxDiagnosisDrawerProps {
  opened: boolean;
  onClose: () => void;
  form: UseFormReturnType<EncounterAnswersFormType>;
  diagnosisToEdit?: EncounterDiagnosesFormType;
}

/** Minimal Coding shape used in the form to avoid Mantine v8 Path<T> recursion via extension → Extension → valueCoding → Coding. */
type DiagnosisCodeEntry = Pick<Coding, "code" | "system" | "display">;

type DiagnosisFormValues = {
  diagnosisCodes: DiagnosisCodeEntry[];
  addToProblemList: boolean;
  notes: string;
};

export default function DxDiagnosisDrawer({
  opened,
  onClose,
  form,
  diagnosisToEdit,
}: DxDiagnosisDrawerProps) {
  const [isSaving, { open: startSaving, close: stopSaving }] = useDisclosure(false);

  const dxForm = useForm<DiagnosisFormValues>({
    initialValues: {
      diagnosisCodes: diagnosisToEdit?.encounterDiagnosisCode
        ? [diagnosisToEdit.encounterDiagnosisCode]
        : [],
      addToProblemList: diagnosisToEdit?.encounterDiagnosisAddToProblemList || false,
      notes: diagnosisToEdit?.encounterDiagnosisNotes || "",
    },
  });

  useEffect(() => {
    if (diagnosisToEdit) {
      dxForm.setValues({
        diagnosisCodes: [diagnosisToEdit.encounterDiagnosisCode],
        addToProblemList: diagnosisToEdit.encounterDiagnosisAddToProblemList || false,
        notes: diagnosisToEdit.encounterDiagnosisNotes || "",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisToEdit]);

  const resetFormData = () => {
    dxForm.reset();
  };

  const handleClose = () => {
    resetFormData();
    onClose();
  };

  const handleSubmit = (values: DiagnosisFormValues) => {
    try {
      if (values.diagnosisCodes.length === 0) {
        return;
      }

      startSaving();

      const shouldAddToProblemList = values.addToProblemList;
      const shouldAddNotes = values.diagnosisCodes.length < 2;

      const newDxPlan = values.diagnosisCodes.map((dx) => ({
        encounterDiagnosisCode: {
          code: dx.code || "",
          system: dx.system || "",
          display: dx.display || "",
        },
        encounterDiagnosisAddToProblemList: shouldAddToProblemList,
        encounterDiagnosisNotes: shouldAddNotes ? values.notes : undefined,
      }));

      if (diagnosisToEdit) {
        const updatedDxPlan = form.values.encounterClientDiagnoses?.map((dx) => {
          if (isEqual(dx, diagnosisToEdit)) {
            return newDxPlan[0]; // newDxPlan is an array of one object
          }
          return dx;
        });

        form.setFieldValue("encounterClientDiagnoses", updatedDxPlan);
      } else {
        form.setFieldValue("encounterClientDiagnoses", [
          ...(form.values.encounterClientDiagnoses ?? []),
          ...newDxPlan,
        ]);
      }

      handleClose();
    } catch (error) {
      showSystemErrorNotification();
      Sentry.captureException(error);
    } finally {
      stopSaving();
    }
  };

  const drawerHeaderTitle = "Add Dx";
  const drawerHeaderSubmitButtonLabel = "Save";

  const handleDiagnosisCodesChange = (value: ValueSetExpansionContains | undefined) => {
    dxForm.setFieldValue("diagnosisCodes", [value as DiagnosisCodeEntry]);
  };

  return (
    <Drawer.Root
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={!isSaving}
      closeOnClickOutside={!isSaving}
    >
      <Drawer.Overlay />
      <form onSubmit={dxForm.onSubmit((values) => handleSubmit(values))}>
        <Drawer.Content>
          <Drawer.Header bg="gray.0">
            <Group justify="space-between">
              <Drawer.CloseButton disabled={isSaving} />
              <Drawer.Title>{drawerHeaderTitle}</Drawer.Title>
            </Group>
            <DisabledReasonButton
              w={90}
              size="xs"
              ms="auto"
              type="submit"
              variant="filled"
              loading={isSaving}
              disabledReason={dxForm.isDirty() || diagnosisToEdit ? undefined : NO_CHANGES_REASON}
            >
              {drawerHeaderSubmitButtonLabel}
            </DisabledReasonButton>
          </Drawer.Header>
          <Drawer.Body>
            <Stack mt="xl" gap="md">
              <ValueSetSearchInput
                creatable={false}
                minInputLength={1}
                name="diagnosisCodes"
                label="Search & Add Dx"
                disabled={!!diagnosisToEdit}
                data-testid="dx-search-input"
                binding={ICD10ValueSets.Clinical}
                onChange={handleDiagnosisCodesChange}
                defaultValue={diagnosisToEdit ? diagnosisToEdit.encounterDiagnosisCode : undefined}
                {...(diagnosisToEdit ? { "data-disabled": true } : {})}
              />
              {dxForm.values.diagnosisCodes?.length > 0 && (
                <Checkbox
                  label="Display on Client Side Panel"
                  defaultChecked={diagnosisToEdit?.encounterDiagnosisAddToProblemList || false}
                  {...dxForm.getInputProps("addToProblemList")}
                />
              )}
              {dxForm.values.diagnosisCodes?.length < 2 && (
                <Textarea autosize minRows={3} label="Notes" {...dxForm.getInputProps("notes")} />
              )}
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </form>
    </Drawer.Root>
  );
}
